import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Checkbox,
  Chip,
  Icon,
  Input,
  Textarea,
  Toast,
  type ChipColor,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader/PageHeader.js";
import { DataTable } from "@/components/DataTable/DataTable.js";
import { PaginationControls } from "@/components/PaginationControls/PaginationControls.js";
import LoadingState from "@/components/LoadingState/index.js";
import EmptyState from "@/components/EmptyState/index.js";
import { useQuery } from "@/hooks/useApi.js";
import { useFeatureFlags } from "@/hooks/useFeatureFlags.js";
import { usePermissions } from "@/hooks/usePermissions.js";
import { ROUTES } from "@/routes.js";
import {
  editorialTransform,
  EDITORIAL_TRANSFORM_JOB_STATUSES,
  type EditorialTransformBackfillResponse,
  type EditorialTransformJob,
  type EditorialTransformJobsQuery,
  type EditorialTransformJobStatus,
  type EditorialTransformOutcome,
  type EditorialTransformQueueStats,
  type EditorialTransformReturnValue,
} from "@/api/editorialTransform.service.js";
import {
  MAX_PARSE_RESULT_IDS,
  analyseIds,
  badgeLabel,
  describeScope,
  dryRunLabel,
  parseLimit,
  pluralise,
  runBackfillLabel,
  scopeNote,
  selectionBody,
  selectionOf,
  type IdLine,
} from "./selection.js";
import styles from "./EditorialTransform.module.css";

const POLL_INTERVAL_MS = 10_000;
const JOBS_PAGE_SIZE = 20;

/** Gates card production server-side: the parse-completion emit and the backfill endpoint. */
const CARD_PRODUCTION_FLAG = "editorial-transform-enabled";

/** EMEA 2027 — the only publication currently in scope, so the backfill field starts on it. */
const DEFAULT_PUBLICATION_ID = "6252202";

type JobsTab = EditorialTransformJobStatus | "previews";

const JOBS_TABS: JobsTab[] = [...EDITORIAL_TRANSFORM_JOB_STATUSES, "previews"];

const STATUS_LABELS: Record<JobsTab, string> = {
  waiting: "Waiting",
  active: "Active",
  completed: "Completed",
  failed: "Failed",
  delayed: "Delayed",
  paused: "Paused",
  previews: "Previews",
};

type OutcomeSeverity = "danger" | "warning" | "neutral" | "muted";

type OutcomeMeta = { label: string; severity: OutcomeSeverity; chipColor: ChipColor };

const OUTCOME_META: Record<EditorialTransformOutcome, OutcomeMeta> = {
  transformed: { label: "Transformed", severity: "neutral", chipColor: "default" },
  content_unchanged: { label: "Unchanged", severity: "neutral", chipColor: "default" },
  manual_entry_protected: {
    label: "Manual entry protected",
    severity: "neutral",
    chipColor: "default",
  },
  edited_row_protected: {
    label: "Edited row protected",
    severity: "neutral",
    chipColor: "default",
  },
  would_insert: { label: "Would insert", severity: "neutral", chipColor: "default" },
  would_update: { label: "Would update", severity: "warning", chipColor: "warning" },
  lawyer_led: { label: "Lawyer-led", severity: "neutral", chipColor: "default" },
  contact_unlinked: {
    label: "Waiting for lawyer link",
    severity: "warning",
    chipColor: "warning",
  },
  unresolved_no_firm_ref: {
    label: "No firm ref (orphan)",
    severity: "warning",
    chipColor: "warning",
  },
  unresolved_firm_ref_not_found: {
    label: "Firm ref not found",
    severity: "danger",
    chipColor: "error",
  },
  company_unresolved: { label: "Company unresolved", severity: "danger", chipColor: "error" },
  failed: { label: "Failed", severity: "danger", chipColor: "error" },
  unknown: { label: "Unknown", severity: "muted", chipColor: "default" },
};

/** Coloured Chip carrying the derived outcome, with the severity exposed on the DOM. */
function OutcomeBadge({
  outcome,
  label: labelOverride,
}: {
  outcome: EditorialTransformOutcome;
  label?: string;
}) {
  const { label, severity, chipColor } = OUTCOME_META[outcome];
  return (
    <span className={styles.outcomeBadge} data-severity={severity} data-outcome={outcome}>
      <Chip size="small" variant="filled" color={chipColor}>
        {labelOverride ?? label}
      </Chip>
    </span>
  );
}

const PREVIEW_SKIP_REASONS: Partial<Record<EditorialTransformOutcome, string>> = {
  content_unchanged: "content unchanged",
  manual_entry_protected: "manual entry protected",
  edited_row_protected: "edited row protected",
  lawyer_led: "lawyer-led",
  contact_unlinked: "waiting for lawyer link",
  unresolved_no_firm_ref: "no firm ref",
};

const PREVIEW_FAILURE_REASONS: Partial<Record<EditorialTransformOutcome, string>> = {
  unresolved_firm_ref_not_found: "firm ref not found",
  company_unresolved: "company unresolved",
};

function previewOutcomeLabel(
  outcome: EditorialTransformOutcome,
  failedReason: string | null,
): string | undefined {
  const skipReason = PREVIEW_SKIP_REASONS[outcome];
  if (skipReason) return `Skipped — ${skipReason}`;
  const failureReason = PREVIEW_FAILURE_REASONS[outcome];
  if (failureReason) return `Failed — ${failureReason}`;
  if (outcome === "failed") return `Failed — ${failedReason ?? "unknown reason"}`;
  return undefined;
}

function formatTimestamp(timestamp: number | null): string {
  if (timestamp == null) return "—";
  return new Date(timestamp).toLocaleString();
}

function MutedCell({ children }: { children: React.ReactNode }) {
  return <span className={styles.mutedCell}>{children}</span>;
}

function ParseResultIdCell({ parseResultId }: { parseResultId: string | null }) {
  if (!parseResultId) return <MutedCell>—</MutedCell>;
  return <span className={styles.jobIdCell}>{parseResultId}</span>;
}

function describeBackfill(result: EditorialTransformBackfillResponse): string {
  if (result.dryRun && result.enqueued > 0) {
    return `Preview queued for ${pluralise(result.enqueued, "document")}. Nothing is written.`;
  }
  if (result.dryRun) {
    return `Dry run: ${result.total} candidate${result.total !== 1 ? "s" : ""} would be enqueued. Nothing was enqueued.`;
  }
  return `Enqueued ${result.enqueued} of ${result.total} candidate${result.total !== 1 ? "s" : ""}, ${result.skipped} skipped.`;
}

function CardProductionDisabledBanner() {
  return (
    <div className={styles.flagBanner} role="status">
      <Icon icon="exclamation" size="small" className={styles.flagBannerIcon} />
      <div className={styles.flagBannerBody}>
        <p className={styles.flagBannerTitle}>Card production is turned off</p>
        <p className={styles.flagBannerText}>
          The <code className={styles.flagBannerCode}>{CARD_PRODUCTION_FLAG}</code> feature flag is
          off, so no editorial-transform cards are being produced — not automatically when a
          document finishes parsing, and not from the backfill below. The queue statistics and job
          list on this page are still live.
        </p>
        <Link to={ROUTES.developer.featureFlags} className={styles.flagBannerLink}>
          Turn the flag on in Feature Flags
        </Link>
      </div>
    </div>
  );
}

/** The field's value as a positive integer, or null when it isn't one yet. */
function parsePublicationId(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

/** Why the backfill controls are blocked, or undefined when they are usable. */
function backfillBlockedReason(
  canBackfill: boolean,
  cardProductionEnabled: boolean,
  flagsLoading: boolean,
  publicationId: number | null,
): string | undefined {
  if (!canBackfill) return "Missing submission.parsing.write permission";
  if (flagsLoading) return `Checking the ${CARD_PRODUCTION_FLAG} feature flag`;
  if (!cardProductionEnabled) return `Turned off by the ${CARD_PRODUCTION_FLAG} feature flag`;
  if (publicationId === null) return "Enter a publication ID first";
  return undefined;
}

const ID_LINE_STATUS_LABELS: Record<Exclude<IdLine["status"], "ok">, string> = {
  "not-uuid": "Not a UUID",
  duplicate: "Duplicate",
  "over-limit": "Over the limit of 50",
};

const MAX_VISIBLE_ID_LINES = 8;

function IdLinesList({ lines }: { lines: IdLine[] }) {
  const flagged = lines.filter(line => line.status !== "ok");
  const visible =
    lines.length > MAX_VISIBLE_ID_LINES ? flagged.slice(0, MAX_VISIBLE_ID_LINES) : lines;
  const hidden = lines.length - visible.length;
  return (
    <div className={styles.idLines}>
      {hidden > 0 && (
        <p className={styles.idLinesMore}>{`… ${pluralise(hidden, "other line")} not shown`}</p>
      )}
      <ul className={styles.idLinesList} data-testid="editorial-transform-id-lines">
        {visible.map(line => (
          <li
            key={line.number}
            className={`${styles.idLine}${line.status !== "ok" ? ` ${styles.idLineFlagged}` : ""}`}
          >
            <span className={styles.idLineNumber}>{line.number}</span>
            <span className={styles.idLineId}>{line.id}</span>
            {line.status !== "ok" && (
              <span className={styles.idLineStatus} data-status={line.status}>
                {ID_LINE_STATUS_LABELS[line.status]}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SelectDocumentsPanel({
  idsText,
  onIdsTextChange,
  limitText,
  onLimitTextChange,
  analysis,
  limit,
  publicationId,
  disabled,
  onClose,
}: {
  idsText: string;
  onIdsTextChange: (text: string) => void;
  limitText: string;
  onLimitTextChange: (text: string) => void;
  analysis: ReturnType<typeof analyseIds>;
  limit: number | undefined | null;
  publicationId: number | null;
  disabled: boolean;
  onClose: () => void;
}) {
  const hasIds = analysis.ids.length > 0;
  const overLimit = analysis.ids.length > MAX_PARSE_RESULT_IDS;
  const recognised = hasIds && !analysis.error && !analysis.warning;
  const showLines = analysis.error !== undefined || analysis.warning !== undefined;
  const limitError =
    !hasIds && limit === null ? "Limit must be a positive whole number" : undefined;
  return (
    <div className={styles.selectPanel} id="editorial-transform-select-documents">
      <div className={styles.selectPanelHeader}>
        <h4 className={styles.selectPanelTitle}>Select documents</h4>
        <Button variant="text" color="secondary" size="small" onClick={onClose}>
          Close
        </Button>
      </div>
      <div className={styles.selectField}>
        <div className={styles.selectFieldHeader}>
          <label
            className={styles.backfillFieldLabel}
            htmlFor="editorial-transform-backfill-parse-result-ids"
          >
            Parse result IDs
          </label>
          <span className={`${styles.idCounter}${overLimit ? ` ${styles.idCounterOver}` : ""}`}>
            {`${analysis.ids.length} / ${MAX_PARSE_RESULT_IDS}`}
          </span>
        </div>
        <Textarea
          id="editorial-transform-backfill-parse-result-ids"
          className={styles.idsTextarea}
          rows={4}
          placeholder="Paste parse result IDs, one per line or separated by spaces or commas"
          value={idsText}
          onChange={event => onIdsTextChange(event.target.value)}
          error={analysis.error}
          helperText={
            hasIds
              ? undefined
              : `Optional. Only parsed results in publication ${publicationId ?? "—"} are picked.`
          }
          disabled={disabled}
          fullWidth
        />
        {analysis.warning && <p className={styles.idsWarning}>{analysis.warning}</p>}
        {recognised && (
          <p
            className={styles.idsRecognised}
          >{`${pluralise(analysis.ids.length, "ID")} recognised`}</p>
        )}
        {showLines && <IdLinesList lines={analysis.lines} />}
      </div>
      <div className={`${styles.selectField} ${styles.limitField}`}>
        <label className={styles.backfillFieldLabel} htmlFor="editorial-transform-backfill-limit">
          Limit
        </label>
        <Input
          id="editorial-transform-backfill-limit"
          inputMode="numeric"
          placeholder="e.g. 10"
          value={limitText}
          onChange={event => onLimitTextChange(event.target.value)}
          error={limitError}
          helperText={
            hasIds
              ? "Not used when IDs are pasted. Clear the IDs to set a limit."
              : "Optional. Takes the oldest N eligible documents."
          }
          disabled={disabled || hasIds}
        />
      </div>
    </div>
  );
}

function PickedCandidates({ result }: { result: EditorialTransformBackfillResponse }) {
  if (result.candidates.length === 0) return null;
  return (
    <div data-testid="editorial-transform-picked">
      <p className={styles.backfillResult}>Picked {result.candidates.length}:</p>
      <ul className={styles.pickedList}>
        {result.candidates.map(candidate => (
          <li key={candidate.parseResultId}>
            {candidate.parseResultId} (submission {candidate.submissionId})
          </li>
        ))}
      </ul>
    </div>
  );
}

function BackfillPanel({
  onBackfilled,
  onViewTab,
  cardProductionEnabled,
  flagsLoading,
}: {
  onBackfilled: () => void;
  onViewTab: (tab: "previews" | "completed") => void;
  cardProductionEnabled: boolean;
  flagsLoading: boolean;
}) {
  const { hasPermission } = usePermissions();
  const canBackfill = hasPermission("submission.parsing.write");
  const [publicationIdText, setPublicationIdText] = useState(DEFAULT_PUBLICATION_ID);
  const publicationId = parsePublicationId(publicationIdText);
  const [panelOpen, setPanelOpen] = useState(false);
  const [parseResultIdsText, setParseResultIdsText] = useState("");
  const [limitText, setLimitText] = useState("");
  const analysis = analyseIds(parseResultIdsText);
  const limit = parseLimit(limitText);
  const selection = selectionOf(analysis.ids, limit);
  const selectionInvalid =
    analysis.error !== undefined || (analysis.ids.length === 0 && limit === null);
  const blockedReason = backfillBlockedReason(
    canBackfill,
    cardProductionEnabled,
    flagsLoading,
    publicationId,
  );
  const disabled = blockedReason !== undefined || selectionInvalid;
  const disabledReason =
    blockedReason ??
    analysis.error ??
    (analysis.ids.length === 0 && limit === null
      ? "Limit must be a positive whole number"
      : undefined);
  const badge = badgeLabel(selection);
  const [forceOverwrite, setForceOverwrite] = useState(false);
  const [running, setRunning] = useState<"dry-run" | "backfill" | null>(null);
  const [result, setResult] = useState<EditorialTransformBackfillResponse | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const runDryRun = async () => {
    if (publicationId === null) return;
    setRunning("dry-run");
    try {
      const dryRunResult = await editorialTransform.backfill({
        dryRun: true,
        publicationId,
        forceOverwrite,
        ...selectionBody(selection),
      });
      setResult(dryRunResult);
      if (dryRunResult.enqueued > 0) {
        onBackfilled();
        onViewTab("previews");
      }
    } catch (err) {
      console.error("Editorial transform dry run failed:", err);
      const message = err instanceof Error ? err.message : "Dry run failed";
      setToast({ message, type: "error" });
    } finally {
      setRunning(null);
    }
  };

  const runBackfill = async () => {
    if (publicationId === null) return;
    const scope = describeScope(selection);
    const confirmed = window.confirm(
      forceOverwrite
        ? `Enqueue an editorial-transform job for ${scope} for publication ${publicationId}? Force overwrite is ON: every existing row is rebuilt from the parsed document and its review status resets to pending — even when nothing changed. Only manual entries are left untouched.`
        : `Enqueue an editorial-transform job for ${scope} for publication ${publicationId}? Unchanged documents are skipped (review progress preserved); changed ones are rebuilt and reset to pending. Manual entries are left untouched.`,
    );
    if (!confirmed) return;

    setRunning("backfill");
    try {
      const backfillResult = await editorialTransform.backfill({
        dryRun: false,
        publicationId,
        forceOverwrite,
        ...selectionBody(selection),
      });
      setResult(backfillResult);
      setToast({
        message: `Enqueued ${backfillResult.enqueued} job${backfillResult.enqueued !== 1 ? "s" : ""}`,
        type: "success",
      });
      onBackfilled();
    } catch (err) {
      console.error("Editorial transform backfill failed:", err);
      const message = err instanceof Error ? err.message : "Backfill failed";
      setToast({ message, type: "error" });
    } finally {
      setRunning(null);
    }
  };

  const previewQueued = result !== null && result.dryRun && result.enqueued > 0;

  return (
    <div className={styles.backfillCard}>
      <div className={`${styles.backfillBody}${panelOpen ? ` ${styles.backfillBodyOpen}` : ""}`}>
        <div className={styles.backfillForm}>
          <h3 className={styles.backfillTitle}>Backfill</h3>
          <p className={styles.backfillDescription}>
            Enqueues an editorial-transform job for every parsed document in the chosen publication,
            rebuilding changed ones and never touching manual entries. A dry run writes nothing.
          </p>
          <div className={styles.backfillField}>
            <label
              className={styles.backfillFieldLabel}
              htmlFor="editorial-transform-backfill-publication-id"
            >
              Publication ID
            </label>
            <Input
              id="editorial-transform-backfill-publication-id"
              inputMode="numeric"
              value={publicationIdText}
              onChange={event => setPublicationIdText(event.target.value)}
              helperText="WordPress publication (edition) id. Defaults to EMEA 2027 (6252202)."
              disabled={running !== null}
            />
          </div>
          <div className={styles.backfillField}>
            <Checkbox
              id="editorial-transform-backfill-force-overwrite"
              label="Force overwrite"
              helperText="Rebuild rows even when the parsed content is unchanged. Without this, unchanged documents are skipped and review progress is preserved."
              checked={forceOverwrite}
              onChange={event => setForceOverwrite(event.target.checked)}
              disabled={running !== null}
            />
          </div>
          <div className={styles.backfillActionsRow}>
            <button
              type="button"
              className={`${styles.selectToggle}${panelOpen ? ` ${styles.selectToggleOpen}` : ""}${badge && !panelOpen ? ` ${styles.selectToggleActive}` : ""}`}
              aria-expanded={panelOpen}
              aria-controls="editorial-transform-select-documents"
              onClick={() => setPanelOpen(open => !open)}
            >
              Select documents
              {badge && (
                <>
                  {" "}
                  <span className={styles.selectBadge}>{badge}</span>
                </>
              )}
              <span className={styles.selectCaret} aria-hidden="true">
                {panelOpen ? "◂" : "▸"}
              </span>
            </button>
            <div className={styles.backfillActions}>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                disabled={disabled || running !== null}
                title={disabledReason}
                onClick={runDryRun}
              >
                {running === "dry-run" ? "Running..." : dryRunLabel(selection)}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={disabled || running !== null}
                title={disabledReason}
                onClick={runBackfill}
              >
                {running === "backfill" ? "Enqueueing..." : runBackfillLabel(selection)}
              </Button>
            </div>
          </div>
          <p className={styles.scopeNote}>{scopeNote(selection)}</p>
          {result && (
            <p
              className={`${styles.backfillResult}${result.dryRun ? ` ${styles.backfillResultDryRun}` : ""}`}
            >
              {describeBackfill(result)}
              {previewQueued && (
                <>
                  {" "}
                  <button
                    type="button"
                    className={styles.resultLink}
                    onClick={() => onViewTab("previews")}
                  >
                    View in Previews
                  </button>
                </>
              )}
              {!result.dryRun && (
                <>
                  {" "}
                  <button
                    type="button"
                    className={styles.resultLink}
                    onClick={() => onViewTab("completed")}
                  >
                    View in Completed
                  </button>
                </>
              )}
            </p>
          )}
          {result && <PickedCandidates result={result} />}
        </div>
        {panelOpen && (
          <SelectDocumentsPanel
            idsText={parseResultIdsText}
            onIdsTextChange={setParseResultIdsText}
            limitText={limitText}
            onLimitTextChange={setLimitText}
            analysis={analysis}
            limit={limit}
            publicationId={publicationId}
            disabled={running !== null}
            onClose={() => setPanelOpen(false)}
          />
        )}
      </div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

type QueueJobRow = {
  id: string;
  parseResultId: string | null;
  outcome: EditorialTransformOutcome;
  firmName: string | null;
  forceOverwrite: boolean;
  attemptsMade: number;
  at: number | null;
  failedReason: string | null;
};

/** Narrows the raw BullMQ returnvalue into the fields the detail panel reads. */
function readReturnValue(returnvalue: EditorialTransformReturnValue | null): {
  companyId: string | null;
  researchBpaId: string | null;
  rowsCreated: number | null;
  skipped: string | null;
  existingRowId: string | null;
} {
  if (returnvalue && "existingRowId" in returnvalue) {
    return {
      companyId: null,
      researchBpaId: null,
      rowsCreated: null,
      skipped: null,
      existingRowId: returnvalue.existingRowId,
    };
  }
  if (returnvalue && "rowsCreated" in returnvalue) {
    return {
      companyId: returnvalue.companyId,
      researchBpaId: returnvalue.researchBpaId,
      rowsCreated: returnvalue.rowsCreated,
      skipped: null,
      existingRowId: null,
    };
  }
  if (returnvalue && "skipped" in returnvalue) {
    return {
      companyId: null,
      researchBpaId: null,
      rowsCreated: null,
      skipped: returnvalue.skipped,
      existingRowId: null,
    };
  }
  return {
    companyId: null,
    researchBpaId: null,
    rowsCreated: null,
    skipped: null,
    existingRowId: null,
  };
}

function DetailValue({ children }: { children: React.ReactNode }) {
  if (children == null || children === "") return <MutedCell>—</MutedCell>;
  return <span className={styles.detailValue}>{children}</span>;
}

/** Submission + result detail for the selected job, sourced from enrichment and returnvalue. */
function JobDetailPanel({ job, onClose }: { job: EditorialTransformJob; onClose: () => void }) {
  const enrichment = job.enrichment;
  const { companyId, researchBpaId, rowsCreated, skipped, existingRowId } = readReturnValue(
    job.returnvalue,
  );
  const submissionId = enrichment?.submissionId ?? job.data?.submissionId ?? null;
  const preview = job.returnvalue && "wouldDo" in job.returnvalue ? job.returnvalue : null;

  return (
    <div className={styles.detailPanel} data-testid="editorial-transform-job-detail">
      <div className={styles.detailHeader}>
        <div className={styles.detailHeaderTitle}>
          <OutcomeBadge outcome={job.outcome} />
          <span className={styles.detailHeaderId}>{job.data?.parseResultId ?? job.id ?? "—"}</span>
        </div>
        <Button variant="text" color="secondary" size="small" onClick={onClose}>
          Close
        </Button>
      </div>
      <dl className={styles.detailGrid}>
        <div className={styles.detailField}>
          <dt className={styles.detailLabel}>Firm name</dt>
          <dd className={styles.detailData}>
            <DetailValue>{enrichment?.firmName}</DetailValue>
          </dd>
        </div>
        <div className={styles.detailField}>
          <dt className={styles.detailLabel}>Firm ref</dt>
          <dd className={styles.detailData}>
            <DetailValue>{enrichment?.firmRef}</DetailValue>
          </dd>
        </div>
        <div className={styles.detailField}>
          <dt className={styles.detailLabel}>Submission ID</dt>
          <dd className={styles.detailData}>
            <DetailValue>{submissionId}</DetailValue>
          </dd>
        </div>
        <div className={styles.detailField}>
          <dt className={styles.detailLabel}>Uploaded by</dt>
          <dd className={styles.detailData}>
            <DetailValue>{enrichment?.createdBy}</DetailValue>
          </dd>
        </div>
        <div className={styles.detailField}>
          <dt className={styles.detailLabel}>Parse result ID</dt>
          <dd className={styles.detailData}>
            <DetailValue>{job.data?.parseResultId}</DetailValue>
          </dd>
        </div>
        <div className={styles.detailField}>
          <dt className={styles.detailLabel}>Company ID</dt>
          <dd className={styles.detailData}>
            <DetailValue>{companyId}</DetailValue>
          </dd>
        </div>
        <div className={styles.detailField}>
          <dt className={styles.detailLabel}>BPA ID</dt>
          <dd className={styles.detailData}>
            <DetailValue>{researchBpaId}</DetailValue>
          </dd>
        </div>
        <div className={styles.detailField}>
          <dt className={styles.detailLabel}>Rows created</dt>
          <dd className={styles.detailData}>
            <DetailValue>{rowsCreated ?? undefined}</DetailValue>
          </dd>
        </div>
        <div className={styles.detailField}>
          <dt className={styles.detailLabel}>Existing row ID</dt>
          <dd className={styles.detailData}>
            <DetailValue>{existingRowId}</DetailValue>
          </dd>
        </div>
        {preview && (
          <>
            <div className={styles.detailField}>
              <dt className={styles.detailLabel}>Ranking table</dt>
              <dd className={styles.detailData}>
                <DetailValue>{preview.rankingTableId}</DetailValue>
              </dd>
            </div>
            <div className={styles.detailField}>
              <dt className={styles.detailLabel}>Office</dt>
              <dd className={styles.detailData}>
                <DetailValue>{preview.officeId}</DetailValue>
              </dd>
            </div>
          </>
        )}
        <div className={styles.detailField}>
          <dt className={styles.detailLabel}>Skip reason</dt>
          <dd className={styles.detailData}>
            <DetailValue>{skipped}</DetailValue>
          </dd>
        </div>
        <div className={styles.detailField}>
          <dt className={styles.detailLabel}>Failure reason</dt>
          <dd className={styles.detailData}>
            <DetailValue>{job.failedReason}</DetailValue>
          </dd>
        </div>
      </dl>
    </div>
  );
}

/** Which of a job's three timestamps actually means something for the selected status. */
function timestampHeader(status: JobsTab): string {
  if (status === "active") return "Started";
  if (status === "completed" || status === "failed") return "Finished";
  return "Created";
}

function jobTimestamp(job: EditorialTransformJob, status: EditorialTransformJobStatus) {
  if (status === "active") return job.processedOn;
  if (status === "completed" || status === "failed") return job.finishedOn;
  return job.timestamp;
}

type TabCounts = Record<JobsTab, number> | undefined;

/** Queue counts with preview jobs taken out of Completed and Failed and gathered under Previews. */
function tabCounts(queue: EditorialTransformQueueStats | null): TabCounts {
  if (!queue) return undefined;
  const previews = queue.previewCounts ?? { completed: 0, failed: 0 };
  return {
    ...queue.counts,
    completed: Math.max(queue.counts.completed - previews.completed, 0),
    failed: Math.max(queue.counts.failed - previews.failed, 0),
    previews: previews.completed + previews.failed,
  };
}

function JobStatusTabs({
  activeTab,
  counts,
  onChange,
}: {
  activeTab: JobsTab;
  counts: TabCounts;
  onChange: (tab: JobsTab) => void;
}) {
  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const size = JOBS_TABS.length;
    let nextIndex: number | null = null;
    if (e.key === "ArrowRight") nextIndex = (index + 1) % size;
    else if (e.key === "ArrowLeft") nextIndex = (index - 1 + size) % size;
    else if (e.key === "Home") nextIndex = 0;
    else if (e.key === "End") nextIndex = size - 1;
    if (nextIndex === null) return;
    e.preventDefault();
    const next = JOBS_TABS[nextIndex];
    onChange(next);
    document.getElementById(`editorial-transform-tab-${next}`)?.focus();
  };

  return (
    <div className={styles.tabs} role="tablist" aria-label="Editorial transform job statuses">
      {JOBS_TABS.map((tab, index) => {
        const isActive = tab === activeTab;
        const count = counts?.[tab];
        return (
          <button
            key={tab}
            type="button"
            role="tab"
            id={`editorial-transform-tab-${tab}`}
            aria-selected={isActive}
            aria-controls="editorial-transform-jobs-panel"
            tabIndex={isActive ? 0 : -1}
            className={`${styles.tab}${isActive ? ` ${styles.tabActive}` : ""}`}
            onClick={() => onChange(tab)}
            onKeyDown={e => handleKeyDown(e, index)}
          >
            {`${STATUS_LABELS[tab]} (${count ?? "—"})`}
          </button>
        );
      })}
    </div>
  );
}

function jobsQueryFor(tab: JobsTab): Pick<EditorialTransformJobsQuery, "status" | "preview"> {
  if (tab === "previews") return { status: "completed", preview: "only" };
  return { status: tab, preview: "exclude" };
}

function JobsBrowser({
  counts,
  tab,
  onTabChange,
}: {
  counts: TabCounts;
  tab: JobsTab;
  onTabChange: (tab: JobsTab) => void;
}) {
  const [page, setPage] = useState(1);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const status = tab;

  const { data, loading, error, refetch } = useQuery(
    () => editorialTransform.getJobs({ ...jobsQueryFor(tab), page, pageSize: JOBS_PAGE_SIZE }),
    [tab, page],
  );

  // refetch closes over the current tab/page, so polling stays on the selected tab.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    const interval = setInterval(() => refetchRef.current(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const columns = useMemo<Column<QueueJobRow>[]>(() => {
    const idColumn: Column<QueueJobRow> = {
      header: "Parse Result ID",
      accessor: "parseResultId",
      render: (_value, row) => <ParseResultIdCell parseResultId={row.parseResultId} />,
    };

    if (status === "previews") {
      return [
        idColumn,
        {
          header: "Firm",
          accessor: "firmName",
          render: (_value, row) => row.firmName || <MutedCell>—</MutedCell>,
        },
        {
          header: "Outcome",
          accessor: "outcome",
          render: (_value, row) => (
            <OutcomeBadge
              outcome={row.outcome}
              label={previewOutcomeLabel(row.outcome, row.failedReason)}
            />
          ),
        },
        {
          header: "Force overwrite",
          accessor: "forceOverwrite",
          render: (_value, row) => (row.forceOverwrite ? "On" : <MutedCell>Off</MutedCell>),
        },
        {
          header: "Finished",
          accessor: "at",
          render: (_value, row) => formatTimestamp(row.at),
        },
      ];
    }

    const base: Column<QueueJobRow>[] = [
      idColumn,
      {
        header: "Outcome",
        accessor: "outcome",
        render: (_value, row) => <OutcomeBadge outcome={row.outcome} />,
      },
      {
        header: "Attempts",
        accessor: "attemptsMade",
      },
      {
        header: timestampHeader(status),
        accessor: "at",
        render: (_value, row) => formatTimestamp(row.at),
      },
    ];

    if (status !== "failed") return base;

    return [
      ...base,
      {
        header: "Reason",
        accessor: "failedReason",
        render: (_value, row) => {
          const reason = row.failedReason ?? "Unknown reason";
          return (
            <span className={styles.jobReasonCell} title={reason}>
              {reason}
            </span>
          );
        },
      },
    ];
  }, [status]);

  const jobs = data?.jobs ?? [];
  const total = data?.pagination.total ?? 0;
  const pageSize = data?.pagination.pageSize ?? JOBS_PAGE_SIZE;
  const hasMore = data?.pagination.hasMore ?? false;
  // Queue counts drift while jobs move between states, so hasMore wins over a stale total.
  const totalPages = Math.max(Math.ceil(total / pageSize), hasMore ? page + 1 : page);
  const startIndex = (page - 1) * pageSize;

  const jobsByRowId = new Map<string, EditorialTransformJob>();
  const rows: QueueJobRow[] = jobs.map((job, index) => {
    const id = job.id ?? `${status}-${startIndex + index}`;
    jobsByRowId.set(id, job);
    return {
      id,
      parseResultId: job.data?.parseResultId ?? null,
      outcome: job.outcome,
      firmName: job.enrichment?.firmName ?? null,
      forceOverwrite: job.data?.forceOverwrite === true,
      attemptsMade: job.attemptsMade,
      at: status === "previews" ? job.finishedOn : jobTimestamp(job, status),
      failedReason: job.failedReason,
    };
  });

  const selectedJob = selectedRowId ? (jobsByRowId.get(selectedRowId) ?? null) : null;

  // The 10s poll refetches without setting `loading`, so it never blanks the table.
  const showLoading = loading;
  const showError = !loading && error != null && rows.length === 0;

  const changeTab = (next: JobsTab) => {
    onTabChange(next);
    setPage(1);
    setSelectedRowId(null);
  };

  const changePage = (next: number) => {
    setPage(next);
    setSelectedRowId(null);
  };

  const toggleRow = (row: QueueJobRow) => {
    setSelectedRowId(current => (current === row.id ? null : row.id));
  };

  return (
    <>
      <h3 className={styles.sectionTitle}>Jobs</h3>
      <JobStatusTabs activeTab={tab} counts={counts} onChange={changeTab} />
      <div
        id="editorial-transform-jobs-panel"
        role="tabpanel"
        aria-labelledby={`editorial-transform-tab-${tab}`}
        className={styles.tableContainer}
      >
        {showLoading && (
          <LoadingState message={`Loading ${STATUS_LABELS[status].toLowerCase()} jobs...`} />
        )}

        {showError && error && (
          <EmptyState variant="error" title="Failed to load jobs" message={error.message} />
        )}

        {!showLoading && !showError && (
          <>
            <DataTable
              data={rows}
              columns={columns}
              rowKey="id"
              size="small"
              onRowClick={toggleRow}
              rowClassName={row => (row.id === selectedRowId ? styles.selectedRow : "")}
              emptyMessage={`No ${STATUS_LABELS[status].toLowerCase()} editorial-transform jobs.`}
            />
            {selectedJob && (
              <JobDetailPanel job={selectedJob} onClose={() => setSelectedRowId(null)} />
            )}
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              startIndex={startIndex}
              endIndex={startIndex + rows.length}
              totalCount={total}
              itemName={status === "previews" ? "previews" : "jobs"}
              onPageChange={changePage}
            />
          </>
        )}
      </div>
    </>
  );
}

function EditorialTransformPage() {
  const { hasFlag, isLoading: flagsLoading } = useFeatureFlags();
  const cardProductionEnabled = hasFlag(CARD_PRODUCTION_FLAG);
  const { data, loading, error, refetch } = useQuery(() => editorialTransform.getQueueStats(), []);

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    const interval = setInterval(() => refetchRef.current(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const [tab, setTab] = useState<JobsTab>("failed");
  const queue = data?.queue ?? null;
  const counts = tabCounts(queue);

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="Editorial Transform" />
      </div>

      {!flagsLoading && !cardProductionEnabled && <CardProductionDisabledBanner />}

      {loading && !queue && <LoadingState message="Loading queue stats..." />}

      {error && !queue && (
        <EmptyState variant="error" title="Failed to load queue stats" message={error.message} />
      )}

      {queue && (
        <div className={styles.queueStatsCard}>
          <div className={styles.queueStatsHeader}>
            <h3 className={styles.queueStatsTitle}>{queue.name}</h3>
            <div className={styles.queueStatsMeta}>
              <span className={styles.queueStatsLastRun}>
                Completed last minute: {queue.completedLastMinute}
              </span>
              <span className={styles.queueStatsLastRun}>
                Last run: {formatTimestamp(queue.lastExecutedAt)}
              </span>
            </div>
          </div>
          <div className={styles.queueCountsGrid}>
            {EDITORIAL_TRANSFORM_JOB_STATUSES.map(key => {
              const value = counts?.[key];
              const isFailed = key === "failed" && value != null && value > 0;
              return (
                <div key={key} className={styles.queueCountItem}>
                  <span className={styles.queueCountLabel}>{STATUS_LABELS[key]}</span>
                  <span
                    className={`${styles.queueCountValue}${isFailed ? ` ${styles.queueCountFailed}` : ""}`}
                  >
                    {value != null ? value : "—"}
                  </span>
                </div>
              );
            })}
          </div>
          <div className={styles.queueStatsFooter}>
            <span className={styles.queueRefreshDot} />
            Auto-refreshing every 10s
          </div>
        </div>
      )}

      <BackfillPanel
        onBackfilled={() => refetchRef.current()}
        onViewTab={setTab}
        cardProductionEnabled={cardProductionEnabled}
        flagsLoading={flagsLoading}
      />

      <JobsBrowser counts={counts} tab={tab} onTabChange={setTab} />
    </div>
  );
}

export default EditorialTransformPage;
